import path from 'path';

import axios, { AxiosError, AxiosInstance } from 'axios';
import { User } from 'oidc-client-ts';

import { userManager } from '~features/authentication';
import { OffsetPagedResults } from '~interfaces';
import { StatisticsFilterParameters } from '~interfaces/requests';
import { PagedResponse } from '~interfaces/responses';
import i18n from '~lib/i18n';

import BaseService from './baseService';

/**
 * The base service to setup basic functionality to communicate
 * with the Topology backend
 */
abstract class TopologyService extends BaseService {
	protected _client: AxiosInstance;

	protected readonly apiVersionPath: string;
	protected readonly path: string = '';

	public get basePath(): string {
		return path.join(this.apiVersionPath, this.path);
	}

	/**
	 *
	 * @param version At the moment of writing Topology may support two api versions.
	 * Pick a version
	 */
	constructor(version: 'v1' | 'v2') {
		super();

		this.apiVersionPath = path.join('api', version);
		this._client = axios.create({
			baseURL: new URL(this.apiVersionPath, import.meta.env.VITE_TOPOLOGY_API_URL).href,
			// Allows the the requests to be cancelled
			signal: this._controller.signal,
		});

		this.initInterceptor();
	}

	/**
	 * Abort the current get requests.
	 * @param reason A reason to abort, e.g. for logging
	 */
	public abortCurrentRequest(reason?: string) {
		this._controller.abort(reason);
		this._controller = new AbortController();

		// Reattach it to the client so we can abort the next request
		this._client.defaults.signal = this._controller.signal;
	}

	/**
	 * If interceptors are defined in the /src/lib folder
	 * that won't work for axios instances. We really have to set it
	 * on the instance
	 * We add the token though an interceptor because if we set it directly
	 * on the axios instance and the token expires? How to refresh? This
	 * seems the more easy and stable way to do it
	 */
	private initInterceptor() {
		let retry = false;

		const identityAuthority = import.meta.env.VITE_IDENTITY_AUTHORITY.replace(/\/$/, '');
		const oidcStorageString = `oidc.user:${identityAuthority}:${import.meta.env.VITE_IDENTITY_CLIENT_ID}`;

		this._client.interceptors.request.use((config) => {
			const oidcStorage = localStorage.getItem(oidcStorageString);
			if (oidcStorage) {
				const user = User.fromStorageString(oidcStorage);
				config.headers['Authorization'] = `Bearer ${user.access_token}`;
				// Add the selected language as header. May influence certain error message
				// and namings of object in the backend
				config.headers['Accept-Language'] = i18n.language;
			}

			return config;
		});

		// If a user does a request, but the access token is expired, it will give a 401. If that happens
		// try to do a silent refresh. E.g. when
		this._client.interceptors.response.use(
			(response) => response,
			async (error: AxiosError) => {
				const originalRequest = error.config;
				if (error.response?.status === 401 && originalRequest != null && !retry) {
					retry = true;

					try {
						await userManager.signinSilent();
					} finally {
						retry = false;
					}

					return this._client(originalRequest);
				}

				return Promise.reject(error);
			}
		);
	}

	/**
	 * Map a Topology paginated response
	 * @param response
	 * @param mapFunction
	 * @returns
	 */
	protected mapPagedResponse<T extends object, U extends object>(
		response: PagedResponse<T>,
		mapFunction: (value: T) => U
	): OffsetPagedResults<U> {
		return {
			...response,
			results: response.results.map((el) => mapFunction(el)),
		};
	}

	/**
	 * Map common filtering parameters.
	 */
	static mapRequestFilterParameters = (args: StatisticsFilterParameters) => {
		const { itemId, categoryId, hubId, partnerId } = args;

		return {
			itemInstanceId: itemId,
			categoryId,
			hubId,
			partnerId,
		};
	};
}

export default TopologyService;
